/**
 * Add/Edit dialog for a Nurse-Saul maturity equation (group).
 *
 * Mirrors the legacy MaturityNurseSaulAdd/Edit forms, and Datum Temperature
 * offers a live °C<->°F helper. The name (Group ID and description) can always
 * be edited; a locked group (the default, or one a finished calibration or
 * started pour uses) keeps its datum, so that field is read-only.
 * A datum outside -10..10 °C is refused (the server would refuse it too); one
 * outside the typical -5..5 °C is allowed with a warning (inputRanges).
 */
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  InputAdornment,
  CircularProgress,
  Alert,
} from '@mui/material';
import { Save as SaveIcon, Lock as LockIcon } from '@mui/icons-material';
import { cToF, fToC } from './tempConvert';
import { DATUM_LIMITS, DATUM_TYPICAL, datumTempLevel, parseNumber } from './inputRanges';
import type { NurseSaulTarget } from '../../services/maturity';

export interface NurseSaulFormValues {
  group_id: string;
  group_description: string;
  datum_temp: string; // °C, held as a string
}

interface Props {
  open: boolean;
  /** The row being edited, or null when adding a new group. */
  editing: NurseSaulTarget | null;
  /** Optional pre-filled values when adding (e.g. from the datum/Q workbench). */
  initialValues?: Partial<NurseSaulFormValues>;
  saving: boolean;
  onClose: () => void;
  onSubmit: (values: NurseSaulFormValues) => void;
}

const EMPTY: NurseSaulFormValues = { group_id: '', group_description: '', datum_temp: '' };

const NurseSaulDialog: React.FC<Props> = ({ open, editing, initialValues, saving, onClose, onSubmit }) => {
  const { t } = useTranslation();
  const [form, setForm] = useState<NurseSaulFormValues>(EMPTY);
  const [tempF, setTempF] = useState<string>('');
  const [errors, setErrors] = useState<{ group_id?: boolean; datum_temp?: boolean }>({});

  useEffect(() => {
    if (!open) return;
    if (editing) {
      const c = String(editing.datum_temp ?? '');
      setForm({
        group_id: editing.group_id,
        group_description: editing.group_description ?? '',
        datum_temp: c,
      });
      setTempF(cToF(c));
    } else {
      const seeded = { ...EMPTY, ...(initialValues ?? {}) };
      setForm(seeded);
      setTempF(cToF(seeded.datum_temp));
    }
    setErrors({});
  }, [open, editing, initialValues]);

  const isEdit = editing !== null;
  const valuesLocked = !!editing?.is_locked;

  const handleCelsius = (value: string) => {
    setForm((prev) => ({ ...prev, datum_temp: value }));
    setTempF(cToF(value));
  };

  const handleFahrenheit = (value: string) => {
    setTempF(value);
    setForm((prev) => ({ ...prev, datum_temp: fToC(value) }));
  };

  // Out of range is shown as soon as a number is typed; blank only after Save.
  // The flag Save sets is used only while the field is blank, so a value typed
  // after a refused Save gets its own error or warning.
  const datumLevel = datumTempLevel(form.datum_temp);
  const datumBlank = parseNumber(form.datum_temp) === null;
  const datumError = datumBlank ? !!errors.datum_temp : datumLevel === 'invalid';
  const datumUnusual = !datumError && datumLevel === 'unusual';
  let datumHelper: string | undefined;
  if (datumBlank) {
    datumHelper = errors.datum_temp ? t('maturityTarget.tempRequired', 'A valid temperature is required') : undefined;
  } else if (datumLevel === 'invalid') {
    datumHelper = t('maturityTarget.datumTempInvalid', {
      defaultValue: 'Enter a datum temperature between {{min}} and {{max}} °C.',
      min: DATUM_LIMITS.min,
      max: DATUM_LIMITS.max,
    });
  } else if (datumUnusual) {
    datumHelper = t('maturityTarget.datumTempUnusual', {
      defaultValue: 'Unusual value: datum temperatures are usually between {{min}} and {{max}} °C. Check it before saving.',
      min: DATUM_TYPICAL.min,
      max: DATUM_TYPICAL.max,
    });
  }

  const handleSubmit = () => {
    const nextErrors = {
      group_id: !form.group_id.trim(),
      datum_temp: !valuesLocked && datumTempLevel(form.datum_temp) === 'invalid',
    };
    setErrors(nextErrors);
    if (nextErrors.group_id || nextErrors.datum_temp) return;
    onSubmit(form);
  };

  return (
    <Dialog open={open} onClose={() => !saving && onClose()} maxWidth="sm" fullWidth>
      <DialogTitle>
        {isEdit
          ? t('maturityTarget.editNurseSaul', 'Edit Nurse-Saul Equation')
          : t('maturityTarget.addNurseSaul', 'Add Nurse-Saul Equation')}
      </DialogTitle>
      <DialogContent sx={{ '&.MuiDialogContent-root': { pt: 3 } }}>
        {valuesLocked && (
          <Alert severity="info" icon={<LockIcon fontSize="inherit" />} sx={{ mb: 2 }} data-testid="values-locked">
            {t('maturityTarget.valuesLocked', {
              defaultValue: '{{reason}} You can still rename it, or use "Duplicate as new" for different values.',
              reason: editing?.lock_reason || t('maturityTarget.locked', 'Locked'),
            })}
          </Alert>
        )}
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              size="small"
              required
              label={t('maturityTarget.groupId', 'Group ID')}
              value={form.group_id}
              onChange={(e) => setForm((p) => ({ ...p, group_id: e.target.value }))}
              error={errors.group_id}
              helperText={errors.group_id ? t('maturityTarget.groupIdRequired', 'Group ID is required') : undefined}
              inputProps={{ maxLength: 50 }}
              slotProps={{ inputLabel: { shrink: true } }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              size="small"
              label={t('maturityTarget.groupDescription', 'Group Description')}
              value={form.group_description}
              onChange={(e) => setForm((p) => ({ ...p, group_description: e.target.value }))}
              inputProps={{ maxLength: 50 }}
              slotProps={{ inputLabel: { shrink: true } }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              size="small"
              required
              type="number"
              label={t('maturityTarget.datumTemp', 'Datum Temperature')}
              value={form.datum_temp}
              onChange={(e) => handleCelsius(e.target.value)}
              disabled={valuesLocked}
              error={datumError}
              helperText={datumHelper}
              InputProps={{ endAdornment: <InputAdornment position="end">°C</InputAdornment> }}
              slotProps={{
                inputLabel: { shrink: true },
                formHelperText: datumUnusual ? { sx: { color: 'warning.dark' } } : undefined,
              }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              size="small"
              type="number"
              label={t('maturityTarget.datumTemp', 'Datum Temperature')}
              value={tempF}
              onChange={(e) => handleFahrenheit(e.target.value)}
              disabled={valuesLocked}
              InputProps={{ endAdornment: <InputAdornment position="end">°F</InputAdornment> }}
              slotProps={{ inputLabel: { shrink: true } }}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          {t('common.cancel', 'Cancel')}
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={saving}
          startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
        >
          {saving ? t('common.saving', 'Saving...') : t('common.save', 'Save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default NurseSaulDialog;
